import { useQuery } from "@tanstack/react-query";
import { fetchQuizBatchAnalytics } from "../services/quizBatchAnalyticsService";

function toNumber(value, fallback = 0) {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : fallback;
}

function normalizeSubjectPerformance(item = {}) {
  return {
    subjectId: toNumber(item?.subject_id),
    subject: item?.subject_name || "Subject",
    strengthLabel: item?.strength_label || "",
    score: toNumber(item?.score),
    totalMarks: toNumber(item?.max_score),
    percentile: toNumber(item?.accuracy_percentage),
    correct: toNumber(item?.correct_answers),
    wrong: toNumber(item?.wrong_answers),
    skipped: toNumber(item?.skipped_answers),
    attemptedQuestions: toNumber(item?.attempted_questions),
    totalQuestions: toNumber(item?.total_questions),
    answeringTime: item?.answer_time_formatted || "00:00:00",
    reviewingTime: item?.review_time_formatted || "00:00:00",
    totalTime: item?.total_time_formatted || "00:00:00",
  };
}

export function useQuizBatchAnalytics(params = {}, options = {}) {
  return useQuery({
    queryKey: ["quizBatchAnalytics", params],
    queryFn: async () => {
      const { payload } = await fetchQuizBatchAnalytics(params);
      const quizDetail = payload?.quiz_detail || {};
      const summary = payload?.summary || {};
      const performanceInsights = payload?.performance_insights || {};
      const guessInsights = payload?.guess_insights || {};

      return {
        quizDetail: {
          quizId: toNumber(quizDetail?.quiz_id),
          assesmentBatchId: toNumber(quizDetail?.assesment_batch_id),
          quizName: quizDetail?.quiz_name || "Quiz",
          status: quizDetail?.status || "",
          submittedAt: quizDetail?.submitted_at || "",
          submittedDate: quizDetail?.submitted_date || "",
          submittedTime: quizDetail?.submitted_time || "",
          userName: quizDetail?.user_name || "Student",
          userImageUrl: quizDetail?.user_image_url || "",
        },
        summary: {
          totalQuestions: toNumber(summary?.total_questions),
          attemptedQuestions: toNumber(summary?.attempted_questions),
          correctAnswers: toNumber(summary?.correct_answers),
          wrongAnswers: toNumber(summary?.wrong_answers),
          skippedAnswers: toNumber(summary?.skipped_answers),
          accuracyPercentage: toNumber(summary?.accuracy_percentage),
          score: toNumber(summary?.score),
          maxScore: toNumber(summary?.max_score),
        },
        subjectWisePerformance: Array.isArray(payload?.subject_wise_performance)
          ? payload.subject_wise_performance.map(normalizeSubjectPerformance)
          : [],
        performanceInsights: {
          totalTimeTaken: performanceInsights?.total_time_taken_formatted || "00:00:00",
          timeForAnswering: performanceInsights?.time_for_answering_formatted || "00:00:00",
          timeForReviewing: performanceInsights?.time_for_reviewing_formatted || "00:00:00",
          averageTimePerAnswer: performanceInsights?.average_time_per_answer || "00:00:00",
        },
        guessInsights: {
          guessCount: toNumber(guessInsights?.guess_count),
          rightGuessCount: toNumber(guessInsights?.right_guess_count),
          wrongGuessCount: toNumber(guessInsights?.wrong_guess_count),
          netGuessScore: toNumber(guessInsights?.net_guess_score),
          positiveFeedback: guessInsights?.positive_feedback || "",
          improvementFeedback: guessInsights?.improvement_feedback || "",
        },
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
